import { useMemo, useRef } from 'react'
import { WarningCircleIcon } from '@phosphor-icons/react'
import { ScoopText, ScoopTable } from '../../../../../../ui'
import { SeverityTag } from '../../../SeverityTag/SeverityTag'
import { severityRank } from '../../../../utils/severity'
import styles from './EntityScorecard.module.scss'

/**
 * Humanize a diagnosis chain like "death_spiral_diagnosis → leading_indicators → ..."
 * to a readable title-cased label.
 */
const humanizeDiagnosis = (diagnosis) => {
    if (!diagnosis) return '—'
    // If it contains arrow chains, take the first segment
    const parts = diagnosis.split(/\s*→\s*/)
    const segment = parts[0].trim()
    // Convert snake_case to Title Case
    return segment.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// Confidence was retired from this scorecard: the engine removed it in v2.9.33
// ("not statistically backed") and group reports carry no real value (audit F2/C1).
// Severity is the engine's quality signal and drives the default ordering.
const buildColumns = (entityTypeLabel) => [
    { field: 'entityId', header: entityTypeLabel, flex: 1, minWidth: 120 },
    {
        field: 'severity',
        header: 'Severity',
        width: 170,
        render: ({ value, row }) => (
            <span className={styles.severityCell}>
                <SeverityTag severity={value} />
                {row.unprobed === true && (
                    // Audit F6 + design D2: an unprobed entity is a WARNING,
                    // not a second severity pill — icon + text, not a chip.
                    <span className={styles.notProbed}>
                        <WarningCircleIcon size={14} className={styles.notProbedIcon} />
                        Not probed
                    </span>
                )}
            </span>
        ),
    },
    {
        field: 'evidence',
        header: 'Evidence',
        flex: 1,
        minWidth: 140,
        valueGetter: ({ row }) => {
            const analyses = row.investigationCount ?? '—'
            const findings = row.keyFindings?.length ?? 0
            return `${analyses} analyses, ${findings} findings`
        },
    },
    {
        field: 'status',
        header: 'Status',
        width: 180,
        render: ({ row }) => (
            <span className={styles.diagnosisCell}>{humanizeDiagnosis(row.diagnosis)}</span>
        ),
    },
    {
        field: 'keyFinding',
        header: 'Key Finding',
        flex: 2,
        minWidth: 200,
        render: ({ row }) => {
            // Two-line clamp instead of an 80-char slice (review D2) — the
            // finding is the most informative column on the page.
            const finding = row.keyFindings?.[0]
            return <span className={styles.clamp2}>{finding || '—'}</span>
        },
    },
]

export const EntityScorecard = ({
    entitySummaries = [],
    entityTypeLabel = 'Entity',
    entitiesNoun = 'Entities',
    onEntityClick,
}) => {
    const tableRef = useRef(null)
    const columns = useMemo(() => buildColumns(entityTypeLabel), [entityTypeLabel])

    // Worst-first by engine severity rank ("Watch" ranks as Mild, etc.) —
    // lexicographic column sorting cannot express this order.
    const sortedRows = useMemo(
        () =>
            [...entitySummaries].sort(
                (a, b) => severityRank(a.severity) - severityRank(b.severity)
            ),
        [entitySummaries]
    )

    if (!entitySummaries.length) return null

    const handleRowClick = (row) => {
        if (onEntityClick) {
            onEntityClick(row.entityId)
        }
    }

    return (
        <div className={styles.entityScorecard}>
            <ScoopText className={styles.sectionTitle}>
                {entitiesNoun} Scorecard ({entitySummaries.length})
            </ScoopText>
            <ScoopTable
                ref={tableRef}
                columns={columns}
                rows={sortedRows}
                onRowClick={handleRowClick}
                domLayout="autoHeight"
                rowHeight={32}
            />
        </div>
    )
}
